import {
  ORDER_PLACED_BY,
  ORDER_CHANNEL,
  statusCodesMap,
  ORDERS_FILTERS_BRAND,
  ORDERS_FILTERS_CHANNEL,
  ORDERS_FILTERS_PLACED_BY,
  ORDERS_FILTERS_STATUS,
  ICON_MAP,
} from 'constants';
import _isFinite from 'lodash/isFinite';
import _camelCase from 'lodash/camelCase';
import moment from 'moment';
import { formatDateMMDDYYYY, getStartDate, getMonth } from './dateUtils';

const calculateTotal = (details = []) => {
  const totalReducer = (acc, cur) => acc + cur;
  const itemsReducer = (acc, cur) => {
    const { quantity = 1, price = 0, tax = 0, freight = 0, duty = 0, other = 0 } = cur;
    return acc + +quantity * (+(price.orderPrice || price) + +tax + +freight + +duty + +other);
  };

  return details
    .map(({ items = [] }) => items.reduce(itemsReducer, 0))
    .reduce(totalReducer, 0)
    .toFixed(2);
};

const getChannel = ({ channel, orderId }) =>
  channel === ORDER_CHANNEL.inStore ? `${channel} - ${orderId.split(':')[0]}` : channel || ORDER_CHANNEL.online;

const parseClientOrderHistory = (data = [], associatePin) => {
  return data.map((item) => {
    const placedByAssociate = item.associatePin === associatePin ? ORDER_PLACED_BY.ME : ORDER_PLACED_BY.OTHER_ASSOCIATE;
    const placedBy = item.associatePin ? placedByAssociate : ORDER_PLACED_BY.CLIENT;
    return {
      orderInfo: {
        'order#': item.formattedOrderId || item.orderId || '',
        date: item.orderDate ? formatDateMMDDYYYY(item.orderDate) : '',
        channel: getChannel(item),
        total: `$${item.totalAmount || '0.00'}`,
        'placed by': placedBy,
      },
      name: null,
      nmvip: item.nmvip,
      vendorId: item.nmvip ? 'NMVIP' : '',
      channel: item.channel?.toLowerCase() || 'online',
      orderId: item.orderId,
    };
  });
};

const parseAssociateOrderHistory = (data = []) => {
  return data.map((item) => {
    const { firstName, lastName, nmvip } = item;
    const name = firstName || lastName ? `${firstName || ''} ${lastName || ''}`.trim() : null;

    return {
      orderInfo: {
        'order#': item.formattedOrderId || item.orderId || '',
        date: item.orderDate ? formatDateMMDDYYYY(item.orderDate) : '',
        channel: getChannel(item),
        total: `$${item.totalAmount || '0.00'}`,
      },
      name,
      nmvip: Boolean(nmvip),
      channel: item.channel?.toLowerCase() || 'online',
      orderId: item.orderId,
    };
  });
};

const sortOrderHistory = (data = {}, sortBy) => {
  const convert = (val) => (val ? +val.toString().replace('$', '') : 0);
  let sortedData = data;

  switch (sortBy) {
    case 'dateDesc':
      sortedData = data.sort((a, b) => new Date(b.orderInfo.date).getTime() - new Date(a.orderInfo.date).getTime());
      break;
    case 'dateAsc':
      sortedData = data.sort((a, b) => new Date(a.orderInfo.date).getTime() - new Date(b.orderInfo.date).getTime());
      break;
    case 'costDesc':
      sortedData = data.sort((a, b) => convert(b.orderInfo.total) - convert(a.orderInfo.total));
      break;
    case 'costAsc':
      sortedData = data.sort((a, b) => convert(a.orderInfo.total) - convert(b.orderInfo.total));
      break;

    default:
      break;
  }

  return sortedData;
};

const itemLevelSortOrderHistory = (data = {}, sortBy) => {
  const convert = (val) => (val ? +val.toString().replace('$', '') : 0);
  let sortedData = data;

  switch (sortBy) {
    case 'dateDesc':
      sortedData = data.sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
      break;
    case 'dateAsc':
      sortedData = data.sort((a, b) => new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime());
      break;
    case 'costDesc':
      sortedData = data.sort((a, b) => convert(b.total) - convert(a.total));
      break;
    case 'costAsc':
      sortedData = data.sort((a, b) => convert(a.total) - convert(b.total));
      break;
    default:
      break;
  }

  return sortedData;
};

const searchOrders = (data = [], searchBy) => {
  const searchValue = typeof searchBy === 'string' ? searchBy.trim().toLowerCase() : '';
  const searchValueReplaced = searchValue.replace(/\//g, ':');
  if (searchValue.length > 0) {
    return data.filter(
      ({ name, firstName = '', lastName = '', orderId = '', orderInfo = {}, formattedOrderId = '', itemSKU = '' }) => {
        const orderNumber = typeof orderInfo['order#'] === 'string' ? orderInfo['order#'].trim().toLowerCase() : '';
        const fullName = (name || `${firstName} ${lastName}`).toLowerCase();
        const searchFields = [orderId, formattedOrderId, itemSKU, fullName, orderNumber];
        const match = searchFields.some((field) => field?.match(RegExp(`${searchValue}|${searchValueReplaced}`, 'gi')));
        return match;
      },
    );
  }
  return data;
};

const isSameLength = (arr1, arr2) => arr1.length === arr2.length;

const checkChannelAgainstFilters = (channel, subChannel, filters) => {
  if (channel === 'In-store' || subChannel === 'CMOS' || subChannel === 'Online' || !subChannel) {
    return filters.includes(channel);
  }
  if (subChannel === 'Connect') {
    return filters.includes('Connect');
  }
};

const checkPlacedByAgainstFilters = (placedBy, order, associatePin) => {
  let passedCheck;
  if (placedBy.includes('Client')) {
    passedCheck = order.associatePin === null;
  }
  if (placedBy.includes('Me')) {
    passedCheck = passedCheck || order.associatePin === associatePin;
  }
  if (placedBy.includes('Other Associate')) {
    passedCheck = passedCheck || (order.associatePin !== null && order.associatePin !== associatePin);
  }
  return passedCheck;
};

const itemLevelFilterBy = (data, associatePin, { date, brand = [], channel = [], placedBy = [] }) => {
  const startDate = getStartDate(date);

  return data.filter((order) => {
    const isDatePassed =
      new Date(order.orderInfo ? order.orderInfo.date : order.orderDate).getTime() > new Date(startDate).getTime();
    let isBrandPassed;
    let isChannelPassed;
    let isPlacedByPassed;

    if (!brand.length || isSameLength(brand, ORDERS_FILTERS_BRAND)) {
      isBrandPassed = true;
    }
    if (!channel.length || isSameLength(channel, ORDERS_FILTERS_CHANNEL)) {
      isChannelPassed = true;
    }
    if (!placedBy.length || isSameLength(placedBy, ORDERS_FILTERS_PLACED_BY)) {
      isPlacedByPassed = true;
    }

    isBrandPassed = isBrandPassed || brand.includes(order.brandLine);
    isChannelPassed = isChannelPassed || checkChannelAgainstFilters(order.channel, order.subChannel, channel);
    isPlacedByPassed = isPlacedByPassed || checkPlacedByAgainstFilters(placedBy, order, associatePin);
    return isDatePassed && isBrandPassed && isChannelPassed && isPlacedByPassed;
  });
};

const lowerCaseArray = (arr) => {
  return arr.map((str) => str.toLowerCase());
};

const newGroupByOrder = (items) => {
  if (!items.length) return [];
  const map = [items[0].orderId];
  const groups = [[items[0]]];
  for (let i = 1; i < items.length; i += 1) {
    const mapVal = map.indexOf(items[i].orderId);
    if (mapVal < 0) {
      groups.push([items[i]]);
      map.push(items[i].orderId);
    } else {
      groups[mapVal].push(items[i]);
    }
  }
  return groups;
};

const groupByOrder = (items) => {
  const groupedItems = items.reduce((acc, val) => {
    const { serviceFlag, orderId, itemSKU = '' } = val;
    const isService = serviceFlag === true || itemSKU?.startsWith('OSP') || itemSKU === null;
    const key = orderId;
    const propertyAlreadyExists = !!acc[key];

    if (propertyAlreadyExists) {
      const newArr = [...acc[key]];
      if (isService) {
        newArr.push(val);
      } else {
        newArr.unshift(val);
      }

      return {
        ...acc,
        [key]: newArr,
      };
    }

    return {
      ...acc,
      [key]: [val],
    };
  }, {});
  return groupedItems;
};

const getSaleAndReturns = (item) => {
  const { totalSales, totalReturns, totalAmount } = item;
  const status = item.status || item.orderStatus;
  const isInStoreOrder = status === 'SALE';
  const isReturn = statusCodesMap[isInStoreOrder ? item.transactionType : status]?.type === 'Returned';
  let returnsPrice = totalReturns;
  let salesPrice = totalSales;
  if (isReturn) {
    returnsPrice = 'totalReturns' in item ? totalReturns : totalAmount;
  } else {
    salesPrice = 'totalSales' in item ? totalSales : totalAmount;
  }
  return { sales: salesPrice, returns: returnsPrice };
};

const itemLevelParse = (orders) => {
  if (orders.length === 0) return {};
  const { subChannel, channel } = orders[0];
  return {
    ...orders[0],
    channel: subChannel === 'Connect' ? subChannel : channel,
    total: orders.filter((e) => e.totalAmount).reduce((a, e) => a + +e.totalAmount, 0),
    fetched: orders.every((val) => val.fetched),
    items: orders.map((order) => {
      const { sales, returns } = getSaleAndReturns(order);
      const { totalAmount, status, orderStatus, itemSKU, skuId, quantity, itemQuantity } = order;
      return {
        ...order,
        itemSKU: itemSKU || skuId,
        quantity: quantity || itemQuantity,
        totalReturns: returns,
        totalSales: sales,
        totalAmount: Math.abs(+totalAmount),
        status: status || orderStatus,
      };
    }),
  };
};

const itemLevelBrowseParse = (orders) => {
  if (orders.length === 0) return {};
  const { subChannel, channel } = orders[0];
  return {
    ...orders[0],
    channel: subChannel === 'Connect' ? subChannel : channel,
    items: orders.map((order) => {
      const { status, orderStatus, itemSKU, skuId, quantity, itemQuantity, brandLine } = order;
      return {
        ...order,
        productDesc: order.product.name,
        thumbnailUrl: order.product.thumbnailUrl,
        imageUrl: order.product.imageUrl,
        brand: brandLine,
        itemSKU: itemSKU || skuId,
        quantity: quantity || itemQuantity,
        status: status || orderStatus,
      };
    }),
  };
};

const groupByMonth = (orders) => {
  if (orders.length === 0) return [];
  const grouped = [];
  let temp = [orders[0]];
  let currMonth = getMonth(orders[0].orderDate);
  for (let i = 1; i < orders.length; i += 1) {
    if (getMonth(orders[i].orderDate) !== currMonth) {
      currMonth = getMonth(orders[i].orderDate);
      grouped.push(temp);
      temp = [orders[i]];
    } else {
      temp.push(orders[i]);
    }
  }
  grouped.push(temp);
  return grouped;
};

const groupBy30Days = (orders) => {
  if (orders.length === 0) return [];
  const within30 = [];
  const olderThan30 = [];
  const now = moment();

  for (let i = 0; i < orders.length; i += 1) {
    const obj = orders[i];
    const end = moment(obj.orderDate);
    const diff = Math.abs(end.diff(now, 'days'));
    if (diff >= 30) {
      obj.age = '>30';
      olderThan30.push(obj);
    }
    if (diff <= 30) {
      obj.age = '<30';
      within30.push(obj);
    }
  }
  return [within30, olderThan30];
};

const throttleFunctionCall = (cb, delayTimer) => {
  let wait = false;

  return (...args) => {
    if (!wait) {
      wait = true;
      cb(...args);
      setTimeout(() => {
        wait = false;
      }, delayTimer);
    }
  };
};

const formatItemSkus = (skus) => ({
  NM: Object.keys(skus.NM),
  BG: Object.keys(skus.BG),
});

/**
 * Create sku hashMap from list of order items
 * @param {array} orders
 * @return {{ NM: {...}, BG: {} }}
 */
const getSKUMapping = (orders = []) => {
  const skuMap = { NM: {}, BG: {} };

  return orders.reduce((acc, curr) => {
    const { brandLine, skuId } = curr;
    if (skuId) acc[brandLine || 'NM'][skuId] = true;

    return acc;
  }, skuMap);
};

/**
 * Create object of product details based on SKU
 * @param {array} details
 * @returns {object}
 */
const transformDetailsFromSKU = (details = []) => {
  return details.reduce((acc, curr) => {
    const sku = !curr.error ? curr.itemskuId : curr.message.substring(curr.message.indexOf('=') + 1);

    return {
      ...acc,
      [sku]: {
        imageUrl: curr.imageUrl || null,
        thumbnailUrl: curr.thumbnailUrl || null,
        productDesc: curr.name || null,
        brand: curr.brand || null,
      },
    };
  }, {});
};

/**
 * Enrich order items with product details data
 * @param {array} items
 * @param {object} skuData
 * @param {Enumerator<string>} skuField ['itemSku', 'skuId']
 */
const addSKUDetails = (items = [], skuData = {}, skuField = 'itemSKU') => {
  return items.map((item) => {
    if (item[skuField]) {
      const currItem = skuData[item[skuField]];

      return {
        ...item,
        imageUrl: currItem?.imageUrl,
        thumbnailUrl: currItem?.thumbnailUrl,
        productDesc: item.productDesc || currItem?.productDesc,
        brand: currItem?.brand,
      };
    }
    return item;
  });
};

const flattenSKUDetails = (items = []) => {
  return items.map((item) => {
    const currItem = item.product;
    return {
      ...item,
      imageUrl: currItem?.imageUrl,
      thumbnailUrl: currItem?.thumbnailUrl,
      productDesc: currItem?.name,
      brand: currItem?.designerName,
    };
  });
};

const convertSort = (sortBy) => {
  switch (sortBy) {
    case 'dateDesc':
      return { sortBy: 'orderDate', sortType: 'desc' };
    case 'dateAsc':
      return { sortBy: 'orderDate', sortType: 'asc' };
    case 'costDesc':
      return { sortBy: 'orderTotal', sortType: 'desc' };
    case 'costAsc':
      return { sortBy: 'orderTotal', sortType: 'asc' };
    default:
      return {};
  }
};

const getFilterBrand = (brand, isClient) => {
  const hasBrand = isClient && brand && brand.length !== ORDERS_FILTERS_BRAND.length;

  if (hasBrand) {
    return brand[0];
  }
};

const getFilterChannels = (channelsArr = []) => {
  const channels = channelsArr.map((channel) => {
    if (channel === 'In-store') return 'instore';
    return _camelCase(channel);
  });
  const channelsLength = channels.length;

  if (channelsLength && channelsLength !== ORDERS_FILTERS_CHANNEL.length) {
    return channels;
  }
};

const getFilterStatus = (statusArr = []) => {
  const status = statusArr.map(_camelCase);
  const statusLength = status.length;

  if (statusLength && statusLength !== ORDERS_FILTERS_STATUS.length) {
    return status;
  }
};

const getFilterPlacedBy = (placedByArr = [], isClient) => {
  const placedByInitialValue = {
    isClient: false,
    isAssociate: false,
    isOtherAssociates: false,
  };

  const getPlacedBy = (initValue, currentValue) => {
    switch (currentValue) {
      case ORDER_PLACED_BY.ME:
        return { ...initValue, isAssociate: true };
      case ORDER_PLACED_BY.OTHER_ASSOCIATE:
        return { ...initValue, isOtherAssociates: true };
      case ORDER_PLACED_BY.CLIENT:
        return { ...initValue, isClient: true };
      default:
        return initValue;
    }
  };

  const placedBy = placedByArr.reduce(getPlacedBy, placedByInitialValue);
  const placedByValues = Object.values(placedBy).filter((val) => val);
  const hasPlacedBy = isClient && placedByValues.length && placedByValues.length !== ORDERS_FILTERS_PLACED_BY.length;

  if (hasPlacedBy) {
    return placedBy;
  }
};

const mapForFetchPayload = (filterBy, searchBy, sortBy, userType, isClient, pageNumber, size) => ({
  ...userType,
  searchText: searchBy || undefined,
  pagination: {
    from: pageNumber?.current,
    size,
  },
  filter: {
    brand: getFilterBrand(filterBy?.brand, isClient),
    channels: getFilterChannels(filterBy?.channel),
    status: getFilterStatus(filterBy?.status),
    placedBy: getFilterPlacedBy(filterBy?.placedBy, isClient),
  },
  sort: convertSort(sortBy),
});

const verifyUserType = (userType) => {
  const testKeys = ['cmosIds', 'posIds', 'webProfileIds'];
  return testKeys.some((key) => userType[key]?.length);
};

const getStoreNumber = (orderId) => {
  const orderIdStr = orderId.toString();
  let index;
  if (orderIdStr.includes(':')) {
    index = orderIdStr.indexOf(':');
  } else if (orderIdStr.includes('/')) {
    index = orderIdStr.indexOf('/');
  }
  return +orderIdStr.substring(0, index);
};

const formatChannel = (channel, subChannel, brandLine, orderId) => {
  const channels = [channel?.toLowerCase(), subChannel?.toLowerCase()];
  const brand = brandLine || '';
  const channelList = [
    { channel: 'cmos', formattedChannel: 'CMOS' },
    { channel: 'in-store', formattedChannel: `${brand} Store ${getStoreNumber(orderId)}` },
    { channel: 'online', formattedChannel: `${brand} Online` },
    { channel: 'connect', formattedChannel: `${brand} Connect` },
  ];
  return channelList.reduce((acc, curr) => {
    if (channels.includes(curr.channel)) return curr;
    return acc;
  }, {});
};

const determineCorrectPriceFlag = (itemTotal, statusCode, totalSales, totalAmount, totalReturns, isNegative) => {
  if (_isFinite(itemTotal)) return Math.abs(itemTotal);
  let price;
  if (statusCode === 'CX') {
    price = -Math.abs(totalSales);
  } else if (isNegative) {
    const fallback = totalReturns || totalSales || totalAmount;
    price = -Math.abs(fallback);
  } else {
    price = Math.abs(totalSales);
  }
  return price;
};

const formatStatus = (isBops, status, subStatus, type) => {
  if (subStatus && isBops) {
    if (subStatus.toLowerCase() === 'item returned') return 'Package Picked Up';
    return subStatus;
  }
  if (status.text) {
    return `${type} (${status.text})`;
  }
  return type;
};

const formatIconColor = (bopsColor, type, icon) => {
  const selectedIcon = ICON_MAP[type || icon];
  return bopsColor || selectedIcon?.color;
};

export {
  calculateTotal,
  parseClientOrderHistory,
  parseAssociateOrderHistory,
  sortOrderHistory,
  searchOrders,
  itemLevelSortOrderHistory,
  itemLevelFilterBy,
  groupByOrder,
  groupByMonth,
  itemLevelBrowseParse,
  itemLevelParse,
  lowerCaseArray,
  throttleFunctionCall,
  formatItemSkus,
  getSKUMapping,
  transformDetailsFromSKU,
  addSKUDetails,
  flattenSKUDetails,
  newGroupByOrder,
  convertSort,
  mapForFetchPayload,
  verifyUserType,
  getStoreNumber,
  formatChannel,
  determineCorrectPriceFlag,
  formatStatus,
  formatIconColor,
  groupBy30Days,
};
